import type { Messages } from './en';

/**
 * Fase E (paquet CKS): la Plaça de la Llibertat / Sala Commemorativa de Chiang Kai-shek i les aigües termals de Beitou.
 * Les línies de pràctica d’anglès es queden en anglès expressament.
 */
export const caCks: Pick<Messages, 'cks'> = {
  cks: {
    mapNames: { cks: 'Plaça de la Llibertat', beitou: 'Aigües termals de Beitou' },
    zones: {
      ck_hall: 'Sala Commemorativa de Chiang Kai-shek', ck_gate: 'Porta de la Plaça de la Llibertat', ck_theater: 'Teatre Nacional', ck_concert: 'Sala Nacional de Concerts',
      ck_guanghua: 'Estany Guanghua', ck_yunhan: 'Estany Yunhan', ck_square: 'Plaça de la Llibertat', ck_garden: 'Jardins de la Sala Commemorativa',
      ck_valley: 'Vall Termal', ck_millennium: 'Banys Termals del Mil·lenni', ck_plumGarden: 'Jardí dels Pruners', ck_library: 'Biblioteca de Beitou',
      ck_museum: 'Museu de les Aigües Termals', ck_btStation: 'Estació de Xinbeitou', ck_hotels: 'Hotels termals', ck_park: 'Parc de Beitou',
    },
    objects: {
      ck_mrt: 'MRT: estació Sala Commemorativa de CKS', ck_gate: 'Racó per fer fotos: Porta de la Plaça de la Llibertat', ck_steps: 'Els 89 esglaons', ck_guard: 'Relleu de la guàrdia',
      ck_hall: 'Placa de la Sala Commemorativa', ck_theater: 'Teatre Nacional', ck_concert: 'Sala Nacional de Concerts', ck_koi: 'Les koi de l’estany Guanghua',
      ck_yunhan: 'Estany Yunhan', ck_taichi: 'Taitxí al matí', ck_kite: 'Venedor d’estels', ck_snack: 'Carretó de pastissets d’ou', ck_bubble: 'Parada de te de bombolles',
      ck_corridor: 'Tauler del passadís', ck_dance: 'Grup de ball del vespre', ck_pigeons: 'Coloms',
      ck_btMrt: 'MRT: estació de Xinbeitou', ck_oldStation: 'Antiga estació de Xinbeitou', ck_library: 'Biblioteca Pública de Beitou',
      ck_museum: 'Museu de les Aigües Termals de Beitou', ck_bath: 'Banys Termals del Mil·lenni', ck_footbath: 'Bany de peus gratuït', ck_valley: 'Vall Termal',
      ck_eggs: 'Ous termals', ck_plum: 'Jardí dels Pruners', ck_stream: 'Riera de Beitou',
    },
    npc: {
      ck_guard: 'Guàrdia d’honor', ck_guide: 'Guia turística', ck_taichi: 'Mestre de taitxí', ck_taichi2: 'L’àvia del taitxí', ck_kiteUncle: 'L’oncle dels estels',
      ck_dancer: 'Cap del grup de ball', ck_teacher: 'Mestra d’excursió', ck_tourist: 'Motxiller', ck_photographer: 'Fotògraf',
      ck_koiGrandma: 'L’àvia de les koi', ck_student: 'Estudiant universitària', ck_volunteer: 'Guia voluntari',
      ck_librarian: 'Bibliotecària', ck_attendant: 'Encarregat dels banys', ck_eggAuntie: 'La tieta dels ous', ck_hikerA: 'Excursionista', ck_hikerB: 'Avi excursionista',
      ck_footGrandpa: 'L’avi Lin', ck_museumGuide: 'Guia del museu', ck_stationVol: 'Voluntària de l’estació', ck_tourist2: 'Visitant del Japó', ck_kid: 'Xiaoan',
    },
    items: {} as Record<string, { name: string; desc: string }>,

    // --- Comuns ----------------------------------------------------------------------------------
    listen: '🔊 Tornar a escoltar',
    next: '▶ Següent',
    ok: 'D’acord!',
    notNow: 'Ara no',
    learned: 'Has après una cosa nova! ⭐',
    poor: 'No tens prou monedes.',

    // --- Plaça de la Llibertat -----------------------------------------------------------------------
    welcomeCks: '🏯 La Plaça de la Llibertat! La sala blanca amb la teulada blava és al fons, pujant 89 esglaons.',
    gate: {
      text: 'La Porta de la Plaça de la Llibertat fa 30 metres d’alçada i 80 d’amplada. Té cinc arcs i teulades de teules blaves. Les lletres grosses 自由廣場 volen dir «Plaça de la Llibertat».',
      photo: '📸 Demanar a algú que et faci una foto',
      how: 'Com ho demanes? (pràctica d’anglès)',
      ask: ['Excuse me, could you take a photo of me, please?', 'Hey. Photo. Me. Now.', 'You take picture, I stand.'], // English practice
      wrong: 'La turista dubta: “Er… could you ask more politely?”',
      done: '“Say cheese!” 📸 Clic! Una foto perfecta amb la porta al darrere.',
    },
    steps: {
      text: 'Hi ha 89 esglaons fins a la Sala Commemorativa: un per cada any de la vida de Chiang Kai-shek. Els pots pujar tots?',
      climb: '🧗 Pujar els 89 esglaons',
      quiz: '❓ Pregunta',
      counting: '“…eighty-seven, eighty-eight, eighty-nine!” Ho has aconseguit! Tota la plaça és als teus peus, fins a la porta.', // English practice
      countEn: 'Eighty-seven, eighty-eight, eighty-nine! I made it!', // English practice
      tired: 'Estàs massa cansat per pujar 89 esglaons. Descansa una mica primer!',
      q: 'How many steps are there?', // English practice
      a: ['Eighty-nine', 'Forty-two', 'One hundred and one'], // English practice
      right: 'Correcte! Vuitanta-nou esglaons. Que bé que comptes!',
      wrong: 'Gairebé. Torna a comptar… un, dos, tres…',
    },
    guard: {
      notNow: (next: string) => `La guàrdia d’honor fa el relleu cada hora en punt, de 9 del matí a 5 de la tarda. El pròxim és a les ${next}. Torna llavors!`,
      closed: 'La cerimònia ja s’ha acabat per avui. La guàrdia fa el relleu cada hora de 9:00 a 17:00. Torna demà!',
      intro: '🎖️ És el relleu de la guàrdia! El públic fa silenci. Escolta l’explicació:',
      lines: [
        'The new guards march out slowly, step by step. Their boots click on the marble.', // English practice
        'Now they spin their rifles in the air — and they never drop them!', // English practice
        'The old guards and the new guards salute each other.', // English practice
        'The new guards stand completely still. They must not move, talk or smile for one hour!', // English practice
      ],
      done: 'Quin espectacle! Els guàrdies entrenen durant mesos per moure’s junts com si fossin una sola persona.',
      watch: '👀 Mirar',
    },
    hall: {
      text: 'La Sala Commemorativa Nacional de Chiang Kai-shek fa 70 metres d’alçada, més o menys com un edifici de 20 pisos. És de marbre blanc i té una teulada de teules vidriades blaves.',
      q: 'How many sides does the roof have?', // English practice
      a: ['Eight', 'Six', 'Four'], // English practice
      right: 'Sí! La teulada és octogonal: té vuit costats, com un senyal de stop.',
      wrong: 'Torna a mirar la teulada i compta’n els costats!',
    },
    theater: {
      text: 'El Teatre Nacional sembla un palau antic, amb la teulada daurada i columnes vermelles. Aquí s’hi fan òperes, ballets i obres de teatre.',
      ask: '🎭 Preguntar per les entrades',
      vendor: 'Hello! Can I help you?', // English practice
      ok: 'Two tickets for tonight’s show, please.', // English practice
      bad: ['Give ticket.', 'Show. Two. Me.'], // English practice
      done: '“Here you are: two tickets, row C. The show starts at seven thirty!” (Te les guardes de record.)', // English practice
    },
    concert: {
      text: 'La Sala Nacional de Concerts és la bessona del teatre. Aquí hi toquen orquestres, corals i pianistes.',
      listen: '🎻 Escoltar des de la porta',
      heard: 'A través de les portes sents violins assajant. Preciós! 🎶',
      phrase: 'What time does the concert start? — It starts at seven o’clock.', // English practice
    },
    koi: {
      text: 'L’estany Guanghua és ple de koi: taronges, blanques, negres i daurades. Algunes koi viuen més de 50 anys!',
      feed: '🐟 Comprar menjar i donar-ne a les koi (5 monedes)',
      fed: 'Xof! Desenes de koi hi arriben corrents i obren la boca: «O! O! O!»',
      en: 'Look! The koi are opening their mouths!', // English practice
      already: 'Les koi ja estan tipes. No els en donis massa!',
    },
    yunhan: {
      text: 'Yunhan vol dir «estany de la Via Làctia». El travessa un pontet, i les tortugues prenen el sol a les roques.',
      en: 'Look, there’s a turtle on the rock!', // English practice
      q: 'What animal is sunbathing on the rock?', // English practice
      a: ['A turtle', 'A cat', 'A pigeon'], // English practice
      right: 'Sí, una tortuga! S’està escalfant al sol.',
      wrong: 'Mira-t’ho bé: té closca!',
    },
    taichi: {
      text: 'Cada matí, els avis i les àvies practiquen taitxí aquí. Es mouen a poc a poc, com l’aigua.',
      join: '🧘 Afegir-s’hi',
      en: 'Breathe in… breathe out… move your arms slowly, like a cloud.', // English practice
      done: 'Et sents tranquil i despert. Tens el cos calentó!',
      later: 'El grup de taitxí es troba cada matí de 6 a 10. Vine d’hora!',
      already: 'Avui ja has fet taitxí. Les teves cames et donen les gràcies!',
    },
    kite: {
      text: 'L’oncle dels estels ven estels de colors: dracs, papallones i peixos. A la plaça oberta fa molt de vent.',
      buy: '🪁 Comprar i fer volar un estel (15 monedes)',
      en: 'The wind is strong today! Let out more string!', // English practice
      done: 'El teu estel puja i puja, i la seva cua llarga balla amb el vent!',
      words: 'Paraules noves: kite · string · tail · wind', // English practice
    },
    snack: {
      vendor: 'Hi! Egg cakes, fresh and warm. What would you like?', // English practice
      ok: 'Can I have a bag of egg cakes, please?', // English practice
      bad: ['Egg cake. Give.', 'I want eat cake now.'], // English practice
      done: 'Here you go! Careful, they’re hot. Enjoy!', // English practice
    },
    bubble: {
      vendor: 'Hello! What can I get you?', // English practice
      ok: 'A medium bubble milk tea with less sugar, please.', // English practice
      bad: ['Tea! Big! Now!', 'Bubble give me.'], // English practice
      done: 'One medium bubble milk tea, less sugar. Here’s your straw!', // English practice
    },
    corridor: {
      intro: 'En un tauler del passadís de teules blaves hi ha un test per als visitants.',
      qs: [
        { q: 'What colour is the Memorial Hall’s roof?', a: ['Blue', 'Red', 'Green'] }, // English practice
        { q: 'Which building has a golden roof?', a: ['The National Theater', 'The MRT station', 'The gate'] }, // English practice
        { q: 'Where do the koi live?', a: ['In the ponds', 'In the trees', 'On the roof'] }, // English practice
      ],
      right: 'Correcte! ✅',
      wrong: 'Mmm, aquesta no. Torna-ho a provar!',
      done: 'Has respost totes les preguntes! El tauler diu: “Thank you for visiting!”',
    },
    dance: {
      text: 'Cada vespre, grups de ballarins assagen davant del teatre amb un altaveu petit que posa música.',
      join: '💃 Afegir-se al ball',
      en: 'Step to the left, step to the right, and turn around!', // English practice
      done: 'Has ballat amb el grup! Les senyores t’aplaudeixen.',
      later: 'Els grups de ball vénen al vespre, de 6 a 10. Assagen per a espectacles i concursos!',
    },
    pigeons: {
      text: 'Centenars de coloms es passegen per la plaça. Quan alguna cosa els espanta, surten volant tots alhora!',
      watch: '👀 Mirar els coloms',
      en: 'Please don’t chase the pigeons!', // English practice
      done: 'Flap, flap, flap! Un núvol de coloms fa una volta a la plaça i torna a posar-se.',
    },

    // --- Beitou ----------------------------------------------------------------------------------------
    welcomeBeitou: '♨️ Benvingut a Beitou! Sents l’olor de les aigües termals? De la riera en surt vapor.',
    oldStation: {
      text: 'Aquesta estacioneta de fusta es va construir el 1916. El 1988 se la van endur a un altre lloc i el 2017 va tornar a casa. Ara és un petit museu.',
      en: 'This station is over one hundred years old.', // English practice
      q: 'What is the old station made of?', // English practice
      a: ['Wood', 'Stone', 'Glass'], // English practice
      right: 'Sí, de fusta! Taulons de fusta a l’estil japonès i una teulada de teules.',
      wrong: 'Mira les parets: taulons marrons…',
    },
    library: {
      text: 'La Biblioteca de Beitou va ser la primera biblioteca ecològica de Taiwan. És de fusta, té plaques solars a la teulada i recull l’aigua de la pluja per a les cisternes dels vàters!',
      read: '📖 Llegir una estona',
      readDone: 'Llegeixes un llibre sobre volcans sota el balcó de fusta. Molt tranquil, molt agradable.',
      en: 'Excuse me, where can I find books in English?', // English practice
      reply: '“They’re on the second floor, next to the window.”', // English practice
      ask: '🗣️ Preguntar a la bibliotecària',
      q: 'What do the solar panels on the roof make?', // English practice
      a: ['Electricity', 'Water', 'Books'], // English practice
      right: 'Correcte! La llum del sol es converteix en electricitat.',
      wrong: 'Pensa-hi: el sol brilla sobre les plaques i produeix…',
      quiz: '❓ Pregunta',
    },
    museum: {
      text: 'El Museu de les Aigües Termals es va construir el 1913 com a bany públic, el més gran de l’Àsia Oriental. A baix (maó vermell) hi ha el gran bany d’estil romà; a dalt (fusta), una sala de tatami per descansar.',
      en: 'Is the museum free? — Yes, it’s free!', // English practice
      q: 'What was the museum before?', // English practice
      a: ['A public bathhouse', 'A train station', 'A school'], // English practice
      right: 'Exacte! La gent hi venia a banyar-se fa més de cent anys.',
      wrong: 'Pista: a baix hi ha un bany enorme…',
    },
    bath: {
      text: 'Els Banys Termals del Mil·lenni són un bany públic a l’aire lliure al costat de la riera. Tothom porta banyador, i les basses van de tèbies a molt calentes.',
      go: '♨️ Banyar-se una hora (40 monedes)',
      rule: 'L’encarregat pregunta: “What must you wear in the pools?”', // English practice
      a: ['A swimsuit', 'My school uniform', 'A raincoat'], // English practice
      wrong: '“No, no! Try again.”',
      done: 'Primer et dutxes i després t’hi fiques a poc a poc, a la bassa tèbia. Aaah… Tot el cos se’t relaxa. 😌',
      en: 'Please take a shower before you get in the pool.', // English practice
      already: 'Avui ja t’has banyat. Massa aigua calenta mareja!',
      closed: 'Els Banys Termals del Mil·lenni ara són tancats. Obren a les 5:30 del matí.',
    },
    footbath: {
      text: 'Un bany de peus públic i gratuït! Seu a la vora de fusta i fica els peus a l’aigua calenteta.',
      soak: '🦶 Remullar-se els peus',
      phrases: [
        'The water is hot! Let me put my feet in slowly.', // English practice
        'It’s warm and cosy. I love it!', // English practice
        'Ouch, it’s too hot! Is it forty-two degrees?', // English practice
        'Ahh, my feet feel so good after walking all day.', // English practice
      ],
      done: 'Els peus se’t posen rosats i calentons. Et sents descansat! (+energia, +ànim)',
      already: 'Ja tens els peus arrugats com panses. Amb una vegada al dia n’hi ha prou!',
      q: 'The water is 42°C. Is it hot or cold?', // English practice
      a: ['It’s hot', 'It’s cold', 'It’s frozen'], // English practice
      right: 'Sí, és calenta! Hot · warm · cool · cold.', // English practice
      wrong: 'Mmm, 42 graus és més que el teu cos (37)…',
      quiz: '🌡️ Test de temperatura',
    },
    valley: {
      text: 'Vall Termal: l’aigua verd jade bombolleja fins a 90 °C! El vapor fa olor de sofre, com d’ous podrits. Abans en deien «la Vall de l’Infern».',
      en: 'Danger! Do not touch the water. It’s very hot!', // English practice
      q: 'How hot can the water in Thermal Valley get?', // English practice
      a: ['About ninety degrees', 'About twenty degrees', 'About zero degrees'], // English practice
      right: 'Sí, gairebé bullint! Per això hi ha una barana.',
      wrong: 'Torna a mirar el vapor i els senyals de perill…',
      fact: 'Aquí hi creix un mineral verd molt rar anomenat pedra de Beitou (hokutolita). Només es troba en dos llocs del món!',
    },
    eggs: {
      vendor: 'Hot-spring eggs! Cooked in the hot water. How many would you like?', // English practice
      ok: 'Three eggs, please.', // English practice
      bad: ['Egg. Three. Now.', 'Give me egg you.'], // English practice
      done: 'Here you are, three warm eggs with a little salt. The yolk is soft and creamy!', // English practice
    },
    plum: {
      text: 'El Jardí dels Pruners (梅庭) era la casa d’estiu del cal·lígraf Yu Youren. La flor del pruner és la flor nacional de Taiwan.',
      en: 'The plum blossom is the national flower of Taiwan.', // English practice
      q: 'When do plum flowers bloom?', // English practice
      a: ['In winter', 'In summer', 'Never'], // English practice
      right: 'Sí! Floreixen amb el fred de l’hivern, per això simbolitzen ser valent i fort.',
      wrong: 'Pista: floreixen quan fa fred…',
    },
    stream: {
      text: 'L’aigua de la riera de Beitou és calenta! L’aigua termal baixa des de la Vall Termal, i els dies frescos hi sura vapor per sobre.',
      en: 'What’s that smell? — It’s sulphur from the hot springs.', // English practice
    },

    // --- Personatges -----------------------------------------------------------------------------------
    talk: {
      ck_guard: [
        { text: 'Estic fent el descans. Dins la sala ens estem quiets una hora sencera. Ni moure’s, ni somriure!' },
        { text: 'Els nostres fusells pesen uns quatre quilos. Els fem girar cada dia als assajos.' },
        { text: 'Els visitants em pregunten: “How do you stand so still?” I jo els dic: “Practice, practice, practice!”', en: 'Practice, practice, practice!' }, // English practice
      ],
      ck_guide: [
        { text: 'Seguiu la bandera groga, tothom! Per aquí cap a la Sala Commemorativa.', en: 'Follow me, please! This way.' }, // English practice
        { text: 'El teatre i la sala de concerts es miren l’un a l’altre a banda i banda de la plaça, com bessons.' },
        { text: 'Un consell: de nit la sala és preciosa, il·luminada de blanc.' },
      ],
      ck_taichi: [
        { text: 'Bon dia! El taitxí em manté jove. Tinc vuitanta-dos anys!', en: 'Good morning! How are you today?' }, // English practice
        { text: 'Doblega una mica els genolls… empeny l’aire a poc a poc, així.' },
      ],
      ck_taichi2: [
        { text: 'Venim cada matí a les sis, plogui o faci sol.', en: 'Rain or shine!' }, // English practice
        { text: 'Després del taitxí, anem tots a prendre llet de soja i creps d’ou. Boníssim!' },
      ],
      ck_kiteUncle: [
        { text: 'Corre contra el vent i deixa anar el fil a poc a poc!', en: 'Let the string out little by little.' }, // English practice
        { text: 'El meu millor estel és un drac de deu metres. Calen tres persones per fer-lo volar!' },
      ],
      ck_dancer: [
        { text: 'Cinc, sis, set, vuit! Una altra vegada, des del principi!', en: 'Five, six, seven, eight!' }, // English practice
        { text: 'Assagem per a un concurs el mes que ve. T’hi apuntes?' },
      ],
      ck_teacher: [
        { text: 'Classe, no us separeu, si us plau! No correu pels esglaons!', en: 'Stay together, please!' }, // English practice
        { text: 'Avui aprenem la història de Taiwan i els seus edificis.' },
      ],
      ck_tourist: [
        { text: 'Hola! Soc del Canadà. On és l’estació del MRT?', en: 'Excuse me, where is the MRT station?' }, // English practice
        { text: 'És gratis visitar la Sala Commemorativa? Sí que ho és! Gràcies!', en: 'Is it free to visit?' }, // English practice
        { text: 'M’encanta el te de bombolles. En anglès en diem “bubble tea” o “boba”!' }, // English practice
      ],
      ck_photographer: [
        { text: 'La millor foto es fa des d’aquí: tota la porta a l’enquadrament, amb el cel blau al darrere.' },
        { text: 'Somriu i digues “cheese”!', en: 'Smile and say cheese!' }, // English practice
      ],
      ck_koiGrandma: [
        { text: 'Aquella koi taronja tan grossa es diu Sort. Fa deu anys que li dono menjar!' },
        { text: 'Colors de koi: taronja, blanc, negre, daurat. Quin és el teu preferit?', en: 'Which colour is your favourite?' }, // English practice
      ],
      ck_student: [
        { text: 'Estudio al passadís. S’hi està fresc i tranquil, i hi corre l’aire.' },
        { text: 'Practico anglès per a un examen. “How long have you lived in Taipei?”', en: 'How long have you lived in Taipei?' }, // English practice
      ],
      ck_volunteer: [
        { text: 'Benvingut! La sala obre a les nou i tanca a les sis.', en: 'The hall opens at nine and closes at six.' }, // English practice
        { text: 'Les teules blaves i el marbre blanc fan que l’edifici sembli tranquil i majestuós.' },
      ],
      ck_librarian: [
        { text: 'Xxt! Això és una biblioteca. Parla fluixet, si us plau.', en: 'Please speak quietly.' }, // English practice
        { text: 'Els grans balcons de fusta tapen el sol, així que necessitem menys aire condicionat.' },
      ],
      ck_attendant: [
        { text: 'Primer dutxa, després banyador, i res de tovalloles a l’aigua!', en: 'Please shower first.' }, // English practice
        { text: 'La bassa més calenta és a dalt de tot, a uns quaranta-quatre graus. Compte!' },
      ],
      ck_eggAuntie: [
        { text: 'Els meus ous es couen amb aigua termal. Es noten els minerals!' },
        { text: 'Quants ous? Un, dos, tres… Sé comptar en anglès!', en: 'How many eggs would you like?' }, // English practice
      ],
      ck_hikerA: [
        { text: 'He baixat caminant des de Yangmingshan. Allà dalt hi ha volcans!' },
        { text: 'Quan vagis d’excursió, no t’oblidis l’aigua i una gorra.', en: 'Don’t forget your water bottle!' }, // English practice
      ],
      ck_hikerB: [
        { text: 'Setanta anys i encara pujo a la muntanya cada cap de setmana!' },
        { text: 'Després d’una excursió, un bany de peus és el millor premi.', en: 'What a beautiful day for a walk!' }, // English practice
      ],
      ck_footGrandpa: [
        { text: 'Vine, seu! Avui l’aigua està perfecta. A uns quaranta graus.', en: 'The water is just right.' }, // English practice
        { text: 'Quan jo era petit, aquí les aigües termals eren plenes de vapor i de cançons.' },
        { text: 'Hot, warm, cool, cold. Digues-ho amb mi!', en: 'Hot, warm, cool, cold.' }, // English practice
      ],
      ck_museumGuide: [
        { text: 'Treu-te les sabates abans de pujar a la sala de tatami.', en: 'Please take off your shoes.' }, // English practice
        { text: 'Els vitralls de baix tenen més de cent anys.' },
      ],
      ck_stationVol: [
        { text: 'L’estació antiga tenia un trenet que portava els banyistes des de Beitou fins aquí.' },
        { text: 'Mira la finestra de la teulada. Deixa entrar la llum a la sala d’espera.' },
      ],
      ck_tourist2: [
        { text: 'Konnichiwa! Beitou sembla un poble termal del Japó.' },
        { text: 'Sempre tasto els ous termals. Són boníssims!', en: 'They are so good!' }, // English practice
      ],
      ck_kid: [
        { text: 'L’aigua de la riera és calenteta! La mama diu que no hi fiqui les mans.' },
        { text: 'Per què fa olor d’ou? És el sofre! Avui he après aquesta paraula.', en: 'It smells like eggs!' }, // English practice
      ],
    } as Record<string, { text: string; en?: string }[]>,
  },
};
